import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { SettingsFormValues } from 'src/Elements/SystemSettings/Battery/types'
import { BatterySettingsDto, FarmBatteryModel, fetchRscpBattery, settingsFormToDto, updateRscpBatterySettings } from 'src/Libs/batteries'
import { fetchRscpBatteries } from 'src/Libs/batteries/api/batteries.api'
import { useInstallationDevices } from '../../installation/hooks'
import { usePortalMutation, usePortalQueries } from 'src/Libs/api'

interface InstallationBatterySettingsReturnType {
  isLoading: boolean
  updateSettings: (storageSerialNo: string, values: SettingsFormValues, batteryId: string) => Promise<boolean | null>
  batteries: FarmBatteryModel[]
  allHaveErrors: boolean
}
const getStoragesBatterySettingQueryKey = (storageSerialNo?: string): [string, string | undefined] => [
  'flow/storage-battery-settings',
  storageSerialNo,
]

export const useInstallationBatterySettings = (installationId?: number): InstallationBatterySettingsReturnType => {
  const authFetch = useAuthFetchWithResponse()
  const { t } = useCustomTranslation()
  const { enqueueErrorStack } = useSnackbar()
  const queryClient = useQueryClient()

  const devices = useInstallationDevices(installationId)

  const storages: {
    storageId: string
    storageDeviceName: string | null
  }[] = useMemo(
    () =>
      (devices.data?.storages ?? [])
        .filter(storage => storage.deviceId !== null)
        .map(storage => {
          return { storageId: storage.deviceId!, storageDeviceName: storage.deviceName }
        }),
    [devices.data?.storages]
  )

  const sortedStorages = useMemo(() => [...storages].sort((a, b) => a.storageId.localeCompare(b.storageId)), [storages])

  const batteryResults = usePortalQueries(
    sortedStorages.map(storage => ({
      queryKey: getStoragesBatterySettingQueryKey(storage.storageId),
      queryFn: async () => {
        const batteryList = await fetchRscpBatteries(storage.storageId, authFetch)
        const results = await Promise.all(
          (batteryList.value || []).map(async item => ({
            item,
            result: await fetchRscpBattery(item.storageSerialNo, item.batteryIndex, authFetch),
          }))
        )
        const batteriesDecorated = results
          .filter(({ result }) => !result.hasErrors && result.value !== null)
          .map(({ item, result }) => ({
            ...result.value!,
            storageSerialNo: item.storageSerialNo,
            storageName: storage.storageDeviceName,
          }))
          .sort((a, b) => (a.batteryListIndex ?? 0) - (b.batteryListIndex ?? 0))

        return batteriesDecorated
      },
      enabled: !!storage,
    }))
  )
  const batteries = batteryResults.flatMap(result => result.data ?? [])
  const allHaveErrors = batteryResults.length > 0 && batteryResults.every(result => result.isError)

  const updateSettings = async (storageSerialNo: string, values: SettingsFormValues, batteryId: string): Promise<boolean | null> => {
    if (!storageSerialNo || !batteryId) {
      throw new Error(`Invalid activeStorageId or tabIndex (id: ${storageSerialNo}, tabIndex: ${batteryId})`)
    }
    const dto = settingsFormToDto(values)
    return mutateInstallationConfig.mutateAsync({ storageSerialNo, batteryId, dto })
  }

  const mutateInstallationConfig = usePortalMutation<boolean, Error, { storageSerialNo: string; batteryId: string; dto: BatterySettingsDto }>({
    mutationFn: async ({ storageSerialNo, batteryId, dto }) => updateRscpBatterySettings(storageSerialNo, batteryId, dto, authFetch),
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({
        queryKey: getStoragesBatterySettingQueryKey(variables.storageSerialNo),
      }),
    onError: () => {
      enqueueErrorStack(t('common.update-failed-error'))
    },
  })

  const isLoading = devices.isLoading || batteryResults.some(result => result.isLoading) || mutateInstallationConfig.isPending

  return { isLoading, updateSettings, batteries, allHaveErrors }
}
